/**
 * DWG 정리 — 남길 레이어 체크 화면.
 * 체크 = 남김. 서버가 키워드와 AI 로 미리 체크해 주고, 사람이 고친 뒤 나머지를 지운 DXF 를 만든다.
 */

import { useMemo, useState, type KeyboardEvent } from 'react'
import type { DwgCount, DwgLayer } from '../../../services/DwgCleanService'
import {
  deleteNamesOf,
  filterLayers,
  initialKeep,
  setKeepAll,
  toggleKeep,
  type DwgLayerFilter,
} from '../lib/dwgCleanSelection'
import styles from './DwgCleanModal.module.css'

export interface DwgCleanModalProps {
  filename: string
  layers: DwgLayer[]
  counts: DwgCount[] // 도면에서 센 CCTV·기둥·주차면·비상벨·통로등·IP 라벨 수
  ai?: string // AI 추천 결과 한 줄 (실패·미설정이면 그 사유)
  onConfirm: (deleteLayers: string[]) => void
  onCancel: () => void
}

const FILTERS: { value: DwgLayerFilter; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'keep', label: '남김' },
  { value: 'delete', label: '삭제' },
]

function layerMeta(l: DwgLayer): string {
  if (l.ipLabel) return `CCTV IP · ${l.count.toLocaleString()}개`
  return l.locked ? '항상 남김' : `${l.count.toLocaleString()}개`
}

export function DwgCleanModal({ filename, layers, counts, ai, onConfirm, onCancel }: DwgCleanModalProps) {
  const [keep, setKeep] = useState(() => initialKeep(layers))
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<DwgLayerFilter>('all')

  const visible = useMemo(() => filterLayers(layers, keep, query, filter), [layers, keep, query, filter])
  const toDelete = useMemo(() => deleteNamesOf(layers, keep), [layers, keep])
  const keptCount = layers.length - toDelete.length

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') onCancel()
  }

  return (
    <div className={styles.overlay} onKeyDown={handleKeyDown}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="dwg-clean-title">
        <header className={styles.header}>
          <h2 id="dwg-clean-title" className={styles.title}>DWG 정리 — 남길 레이어</h2>
          <p className={styles.filename}>{filename}</p>
          <p className={styles.description}>
            체크한 레이어만 남기고 나머지를 지운 DXF를 만듭니다. 주차구역·CCTV·IP·비상벨·통로등·기둥·외곽선으로
            보이는 이름과, AI 가 개체 종류·블록·과거 현장 매핑을 보고 고른 레이어를 미리 체크했습니다. CCTV IP 라벨
            레이어는 맵에서 IP 매핑에 쓰므로 항상 남깁니다.
          </p>
          {ai && <p className={styles.aiNote}>{ai}</p>}
        </header>

        {counts.length > 0 && (
          <section className={styles.counts} aria-label="도면 수량">
            <ul className={styles.countGrid}>
              {counts.map((c) => (
                <li key={c.kind} className={styles.countTile}>
                  <span className={styles.countLabel}>{c.label}</span>
                  <strong className={styles.countValue}>{c.total.toLocaleString()}</strong>
                </li>
              ))}
            </ul>
            <details className={styles.countDetails}>
              <summary>어디서 셌는지 — 블록 개수로 센 추정치라 선으로만 그린 것은 0 으로 나옵니다</summary>
              {counts
                .filter((c) => c.items.length > 0)
                .map((c) => (
                  <div key={c.kind}>
                    <h3 className={styles.countDetailTitle}>{c.label}</h3>
                    <ul className={styles.countItems}>
                      {c.items.map((it) => (
                        <li key={it.name}>
                          <span>{it.name}</span>
                          <span>{it.count.toLocaleString()}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </details>
          </section>
        )}

        <div className={styles.controls}>
          <input
            type="search"
            className={styles.search}
            placeholder="레이어 이름·문자 검색"
            aria-label="레이어 검색"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className={styles.filters} role="tablist" aria-label="보기">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                role="tab"
                aria-selected={filter === f.value}
                className={styles.filter}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className={styles.bulk}>
            <button type="button" className={styles.bulkButton} onClick={() => setKeep(setKeepAll(keep, visible, true))}>
              보이는 것 남김
            </button>
            <button type="button" className={styles.bulkButton} onClick={() => setKeep(setKeepAll(keep, visible, false))}>
              보이는 것 삭제
            </button>
            <button type="button" className={styles.bulkButton} onClick={() => setKeep(initialKeep(layers))}>
              추천대로
            </button>
          </div>
        </div>

        <ul className={styles.list}>
          {visible.map((l) => {
            const kept = keep.has(l.name)
            return (
              <li key={l.name}>
                <label className={`${styles.row} ${kept ? '' : styles.rowDeleted}`}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={kept}
                    disabled={l.locked}
                    onChange={() => setKeep((prev) => toggleKeep(prev, l))}
                  />
                  <span className={styles.name}>
                    {l.name}
                    {l.aiReason && (
                      <span className={styles.aiReason}>
                        <span className={styles.aiBadge}>AI</span>
                        {l.aiReason}
                      </span>
                    )}
                    {l.shapeReason && (
                      <span className={styles.aiReason}>
                        <span className={`${styles.aiBadge} ${styles.shapeBadge}`}>모양</span>
                        {l.shapeReason}
                      </span>
                    )}
                  </span>
                  <span className={styles.meta}>
                    {layerMeta(l)}
                    {l.sampleText ? ` · “${l.sampleText}”` : ''}
                  </span>
                </label>
              </li>
            )
          })}
          {visible.length === 0 && <li className={styles.empty}>맞는 레이어가 없습니다</li>}
        </ul>

        <footer className={styles.footer}>
          <span className={styles.summary}>
            남김 <strong>{keptCount}</strong> · 삭제 <strong>{toDelete.length}</strong>
          </span>
          <button type="button" className={styles.cancelButton} onClick={onCancel}>
            취소
          </button>
          <button
            type="button"
            className={styles.confirmButton}
            disabled={toDelete.length === 0}
            onClick={() => onConfirm(toDelete)}
          >
            {toDelete.length}개 지우고 계속
          </button>
        </footer>
      </div>
    </div>
  )
}
